import { formatAmount } from "@/lib/helpers/money"
import { ROUTES, segmentsAfterRegion } from "@/lib/routes"
import type { CalculatedProductPrice } from "@/lib/types/products"
import type { RegionInfo } from "@/lib/types/region"
import type { StoreDiscount, StoreProduct, StoreProductVariant, StoreRatingBucket } from "@/platform/api/store-contract"

const getDiscountAmount = (price: number, discounts: StoreDiscount[]): number => {
  let discountAmount = 0

  for (const discount of discounts) {
    if (discount.rule.type === "percentage") {
      discountAmount += (price * discount.rule.value) / 100
    } else if (discount.rule.type === "fixed") {
      discountAmount += discount.rule.value
    }
  }

  return discountAmount
}

const getPercentageDiff = (original: number, calculated: number) => {
  if (original <= 0) return 0
  const diff = original - calculated
  return (diff / original) * 100
}

const getVariantPrice = (variant: StoreProductVariant): number => variant.calculated_price ?? variant.prices[0]?.amount ?? 0

const getVariantOriginalPrice = (variant: StoreProductVariant): number => variant.original_price ?? getVariantPrice(variant)

export function getProductPrice({
  product,
  variantId,
  region,
  discounts,
}: {
  product: StoreProduct
  variantId?: string
  region: RegionInfo
  discounts?: StoreDiscount[]
}): CalculatedProductPrice {
  const cheapestPrice = () => {
    if (!product.variants.length) {
      return null
    }

    const cheapestVariant = product.variants.reduce((prev, curr) => {
      return getVariantPrice(prev) < getVariantPrice(curr) ? prev : curr
    })

    const variantPrice = getVariantPrice(cheapestVariant)
    const originalPrice = getVariantOriginalPrice(cheapestVariant)
    const discountAmount = discounts ? getDiscountAmount(variantPrice, discounts) : 0
    const calculatedPrice = variantPrice - discountAmount

    return {
      raw_calculated_price: calculatedPrice,
      raw_original_price: originalPrice,
      calculated_price: formatAmount({
        amount: calculatedPrice,
        region,
        includeTaxes: false,
      }),
      original_price: formatAmount({
        amount: originalPrice,
        region,
        includeTaxes: false,
      }),
      percentage_diff: getPercentageDiff(originalPrice, calculatedPrice).toFixed(),
      raw_discount_amount: discountAmount,
      formatted_discount_amount: formatAmount({
        amount: discountAmount,
        region,
        includeTaxes: false,
      }),
    }
  }

  const selectedVariantPrice = () => {
    if (!variantId) {
      return null
    }

    const variant = product.variants.find((v) => v.id === variantId || v.sku === variantId)

    if (!variant) {
      return null
    }

    const price = getVariantPrice(variant)
    const originalPrice = getVariantOriginalPrice(variant)
    const discountAmount = discounts ? getDiscountAmount(price, discounts) : 0
    const calculatedPrice = price - discountAmount

    return {
      raw_calculated_price: calculatedPrice,
      raw_original_price: originalPrice,
      calculated_price: formatAmount({
        amount: calculatedPrice,
        region,
        includeTaxes: false,
      }),
      original_price: formatAmount({
        amount: originalPrice,
        region,
        includeTaxes: false,
      }),
      percentage_diff: getPercentageDiff(originalPrice, calculatedPrice).toFixed(),
      raw_discount_amount: discountAmount,
      formatted_discount_amount: formatAmount({
        amount: discountAmount,
        region,
        includeTaxes: false,
      }),
    }
  }

  return {
    product,
    cheapestPrice: cheapestPrice(),
    variantPrice: selectedVariantPrice(),
  }
}
/**
 * Creates standardized list identifiers for analytics tracking
 * Used to generate consistent item_list_id and item_list_name values
 * for Google Analytics VIEW_ITEM_LIST events
 */
interface ListIdentifiers {
  listId: string
  listName: string
}

/**
 * Generate standardized list identifiers based on URL and page context
 *
 * @param path Current URL path
 * @param searchParams URL search parameters (supports both URLSearchParams and Next.js ReadonlyURLSearchParams)
 * @param pageTitle Current document title
 * @returns Standardized list identifiers
 */
export const createListIdentifiers = (path: string, searchParams: { get(name: string): string | null } | null, pageTitle: string): ListIdentifiers => {
  // Default values use the path and title
  let listId = path
  let listName = pageTitle

  // Extract path components
  const urlParts = segmentsAfterRegion(path)

  // Create more specific list ID based on URL structure
  if (urlParts.length > 0) {
    if (urlParts[0] === ROUTES.CATEGORIES) {
      // For category pages
      const categoryPath = urlParts.slice(1).join("_")
      listId = `category_${categoryPath}`
      listName = `Category: ${pageTitle}`
    } else if (urlParts[0] === ROUTES.COLLECTIONS) {
      // For collection pages
      const collectionPath = urlParts.slice(1).join("_")
      listId = `collection_${collectionPath}`
      listName = `Collection: ${pageTitle}`
    } else if (urlParts[0] === ROUTES.SEARCH) {
      // For search pages
      const query = searchParams?.get("search") || "all"
      listId = `search_${query}`
      listName = `Search: ${query}`
    } else if (urlParts[0] === "results") {
      // For search results pages
      const query = urlParts[1] || "all"
      listId = `search_results_${query}`
      listName = `Search Results: ${query}`
    }
  }

  return { listId, listName }
}

/**
 * Read the typed rating/review_count fields api-v2 returns on the priced
 * product (StoreProductDto), never the legacy `metadata` copy.
 */
export const getStoreProductRating = (
  product: Pick<StoreProduct, "rating" | "review_count" | "rating_distribution">
): { average: number; count: number; distribution: StoreRatingBucket[] } | null => {
  const { rating, review_count: reviewCount, rating_distribution: distribution } = product
  if (rating == null || reviewCount == null) {
    return null
  }
  return { average: rating, count: reviewCount, distribution }
}

/**
 * `preview` is a Nelo pre-launch status that the API serves only through the exact-handle lookup. Those links are shared to shoot demo videos before
 * launch, so the page presents the product as purchasable even with no inventory.
 */
export const isPreviewProduct = (product: Pick<StoreProduct, "status">): boolean => product.status === "preview"
